// ──────────────────────────────────────────────
// GeoDrops — Create Pin Sheet
// Bottom sheet for composing a new pin drop.
// Uses Telegram Main Button for submission.
// ──────────────────────────────────────────────

import { useState, useEffect, useCallback } from "react";
import type { PinCategory, CreatePinInput } from "@/types";
import { CATEGORIES } from "@/types";
import {
  showMainButton,
  hideMainButton,
  setMainButtonLoading,
  showBackButton,
  hideBackButton,
  hapticImpact,
  hapticNotification,
  enableCloseConfirmation,
  disableCloseConfirmation,
} from "@/lib/telegram";

interface CreatePinProps {
  lat: number;
  lng: number;
  onSubmit: (input: CreatePinInput) => Promise<void>;
  onCancel: () => void;
}

const EXPIRY_OPTIONS = [
  { value: 15, label: "15 min" },
  { value: 30, label: "30 min" },
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" },
  { value: 240, label: "4 hours" },
  { value: 480, label: "8 hours" },
];

export function CreatePin({ lat, lng, onSubmit, onCancel }: CreatePinProps) {
  const [category, setCategory] = useState<PinCategory>("other");
  const [message, setMessage] = useState("");
  const [expiryMinutes, setExpiryMinutes] = useState(60);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = message.trim().length > 0 && message.length <= 280;

  // Submit handler
  const handleSubmit = useCallback(async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setMainButtonLoading(true);

    try {
      await onSubmit({
        lat,
        lng,
        category,
        message: message.trim(),
        expires_in_minutes: expiryMinutes,
      });
      hapticNotification("success");
      disableCloseConfirmation();
    } catch (err) {
      hapticNotification("error");
      setSubmitting(false);
      setMainButtonLoading(false);
    }
  }, [canSubmit, submitting, lat, lng, category, message, expiryMinutes, onSubmit]);

  // Wire up Telegram Main Button
  useEffect(() => {
    if (canSubmit) {
      showMainButton("Drop Pin", handleSubmit);
    } else {
      hideMainButton();
    }
    return () => hideMainButton();
  }, [canSubmit, handleSubmit]);

  // Wire up Telegram Back Button
  useEffect(() => {
    showBackButton(onCancel);
    return () => hideBackButton();
  }, [onCancel]);

  // Enable close confirmation while composing
  useEffect(() => {
    if (message.length > 0) {
      enableCloseConfirmation();
    }
    return () => disableCloseConfirmation();
  }, [message]);

  return (
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        background: "rgba(8,21,66,0.92)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        borderRadius: "24px 24px 0 0",
        padding: "20px 20px calc(env(safe-area-inset-bottom, 0px) + 20px)",
        boxShadow: "0 -12px 32px rgba(0,0,0,0.4)",
        zIndex: 2000,
        animation: "slideUp 0.2s ease-out",
        maxHeight: "70vh",
        overflowY: "auto",
      }}
    >
      {/* Handle */}
      <div
        style={{
          width: 36,
          height: 4,
          borderRadius: 2,
          background: "#3d466c",
          margin: "0 auto 20px",
        }}
      />

      <h3
        style={{
          margin: "0 0 16px",
          color: "#e1e4ff",
          fontSize: 22,
          fontWeight: 800,
          fontFamily: "var(--font-headline, 'Plus Jakarta Sans', sans-serif)",
          letterSpacing: "-0.02em",
        }}
      >
        Drop a Pin
      </h3>

      {/* Category picker */}
      <label
        style={{ color: "var(--tg-section-header)", fontSize: 13, fontWeight: 500, marginBottom: 8, display: "block" }}
      >
        Category
      </label>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 16,
        }}
      >
        {CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            onClick={() => {
              setCategory(cat.key);
              hapticImpact("light");
            }}
            style={{
              padding: "8px 14px",
              borderRadius: 20,
              border: category === cat.key ? `2px solid ${cat.color}` : "2px solid transparent",
              background: category === cat.key ? `${cat.color}22` : "var(--tg-secondary-bg)",
              color: category === cat.key ? cat.color : "var(--tg-text)",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {cat.emoji} {cat.label}
          </button>
        ))}
      </div>

      {/* Message input */}
      <label
        style={{ color: "var(--tg-section-header)", fontSize: 13, fontWeight: 500, marginBottom: 8, display: "block" }}
      >
        Message
      </label>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="What's here? Keep it short..."
        maxLength={280}
        rows={3}
        autoFocus
        style={{
          width: "100%",
          padding: 12,
          borderRadius: 12,
          border: "none",
          background: "rgba(19,33,86,0.5)",
          color: "#e1e4ff",
          fontSize: 16,
          resize: "none",
          outline: "none",
          fontFamily: "inherit",
          boxSizing: "border-box",
        }}
      />
      <div
        style={{
          textAlign: "right",
          fontSize: 12,
          color: message.length > 260 ? "var(--tg-destructive)" : "var(--tg-hint)",
          marginTop: 4,
          marginBottom: 16,
        }}
      >
        {message.length}/280
      </div>

      {/* Expiry picker */}
      <label
        style={{ color: "var(--tg-section-header)", fontSize: 13, fontWeight: 500, marginBottom: 8, display: "block" }}
      >
        Expires in
      </label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
        {EXPIRY_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => {
              setExpiryMinutes(opt.value);
              hapticImpact("light");
            }}
            style={{
              padding: "8px 14px",
              borderRadius: 20,
              border:
                expiryMinutes === opt.value
                  ? "2px solid var(--tg-button)"
                  : "2px solid transparent",
              background:
                expiryMinutes === opt.value
                  ? "var(--tg-button)"
                  : "var(--tg-secondary-bg)",
              color:
                expiryMinutes === opt.value
                  ? "var(--tg-button-text)"
                  : "var(--tg-text)",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* In-app submit button (always visible — works both inside and outside Telegram) */}
      <button
        onClick={handleSubmit}
        disabled={!canSubmit || submitting}
        style={{
          width: "100%",
          height: 52,
          borderRadius: 9999,
          border: "none",
          background: canSubmit
            ? "linear-gradient(135deg, var(--tg-button, #85adff), #6c9fff)"
            : "var(--tg-secondary-bg, #1e293b)",
          color: canSubmit ? "var(--tg-button-text, #002c65)" : "var(--tg-hint, #6b739d)",
          fontSize: 16,
          fontWeight: 700,
          cursor: canSubmit ? "pointer" : "not-allowed",
          transition: "all 0.2s",
          opacity: submitting ? 0.6 : 1,
          boxShadow: canSubmit ? "0 8px 20px rgba(133,173,255,0.3)" : "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          letterSpacing: "-0.01em",
        }}
      >
        {submitting ? "Dropping..." : "Drop Pulse"}
        {!submitting && <span style={{ fontSize: 18 }}>&#10148;</span>}
      </button>
    </div>
  );
}
